import * as React from 'react';
import * as Immutable from 'immutable';
import {
  DesignerFlux,
  type DesignerStores,
  useDesignerSelector,
  useFluxContext,
} from '@packages/systems/designer/flux';
import {createSmartContext} from '@packages/utilities/smart-context';
import {
  OPEN_CANVAS_ROOT_ID,
  WF_FRAME_ID_ATTRIBUTE,
  useFrameSizeTransitionEffect,
  getBreakpointWidth,
} from '@packages/systems/open-canvas/ui';
import {
  type BreakpointID,
  getBreakpointID,
} from '@packages/systems/style/types';
import {
  CANVAS_BODY_RENDERED,
  STYLE_SHEET_RENDERED,
} from '@packages/systems/designer/actions';
import {usePreviewMode} from '@packages/systems/render';
import {getCodeIslandContentElements} from '@packages/systems/code-libraries/inner-node-utils';

/**
 * A map of frame IDs to their measurements.
 *
 * See also:
 * - {@link useFrameMeasurements}
 * - {@link FrameMeasurementsContext}
 */
type FrameMeasurements = Immutable.Map<
  string,
  {width: number | 'max-content'; height: number | 'max-content'}
>;

/**
 * React context for the frame measurements.
 *
 * This context can be used to specific measurements for a given frame.
 *
 * See also:
 * - {@link useFrameMeasurements}
 * - {@link FrameMeasurements}
 */
const FrameMeasurementsContext = createSmartContext<FrameMeasurements>(
  Immutable.Map()
);

const COLLAPSE_ALL_FRAMES_CLASS = 'wf-collapse-all-frames';

/**
 * Suspends the Fit rules in {@link FRAME_MEASUREMENTS_STYLE_TAG} for the
 * duration of the unhugged probe, so the pass can read every code component
 * unshrunk, at its frame's full width.
 *
 * See also:
 * - {@link useFrameMeasurements}
 */
const SUPPRESS_CODE_ISLAND_HUG_CLASS = 'wf-measure-code-island-unhugged';

/**
 * Gutter left between a code component and the edges of the Fit-width frame
 * that hugs it, so the component is not flush against the frame.
 */
const CODE_COMPONENT_FIT_FRAME_PADDING = 10;

const CODE_ISLAND_TAG_NAME = 'CODE-ISLAND';

function isCodeIsland(element: Element): boolean {
  return element.tagName === CODE_ISLAND_TAG_NAME;
}

/**
 * Marks a code component host that {@link canHugCodeIsland} has ruled out, so
 * the Fit rule in {@link FRAME_MEASUREMENTS_STYLE_TAG} leaves it at its
 * frame's width outside the measurement pass. Written by
 * {@link useFrameMeasurements}, which re-decides it on every pass.
 */
const CODE_ISLAND_NO_HUG_ATTRIBUTE = 'data-wf-code-island-hug';
const CODE_ISLAND_NO_HUG_VALUE = 'none';

/**
 * Bounding rect minus the padding the Fit rule applies, i.e. the box the
 * component itself occupies inside its host. The two probes in
 * {@link useFrameMeasurements} compare these, and only one of them has the
 * padding, so neither axis can be compared without taking it off first.
 */
export function codeIslandContentBoxSize(
  contentEl: Element,
  boundingRect: {width: number; height: number}
): {width: number; height: number} {
  const {paddingLeft, paddingRight, paddingTop, paddingBottom} =
    getComputedStyle(contentEl);

  return {
    width:
      boundingRect.width -
      (parseFloat(paddingLeft) || 0) -
      (parseFloat(paddingRight) || 0),
    height:
      boundingRect.height -
      (parseFloat(paddingTop) || 0) -
      (parseFloat(paddingBottom) || 0),
  };
}

/**
 * The widest box the component paints inside its host. Read while the host is
 * at its full width, so it answers "how much of the space on offer does this
 * component actually take?".
 *
 * The boxes come from {@link getCodeIslandContentElements}, not from the shadow
 * root's own children: those are the `[data-root]` wrapper and any `<style>`,
 * and `[data-root]` is `display: contents`, so every one of them measures zero
 * however large the component is. Reading them would make this return
 * `fullWidth` for every loaded component and hugging would never happen.
 *
 * `fullWidth` is that space on offer, returned when nothing measurable comes
 * back — no content elements, or content that paints no box.
 */
export function measureCodeIslandPaintedWidth(
  contentEl: Element,
  fullWidth: number
): number {
  const {shadowRoot} = contentEl;
  // The light-DOM fallback covers a host whose shadow root is not attached
  // yet, which measures 0 either way.
  const contentElements =
    shadowRoot != null
      ? getCodeIslandContentElements(shadowRoot)
      : Array.from(contentEl.children);

  let widest = 0;
  for (const child of contentElements) {
    widest = Math.max(widest, child.getBoundingClientRect().width);
  }

  // Nothing measurable came back: the component rendered no root elements, or
  // the ones it rendered paint no box. Reporting 0 would read as "takes none of
  // the space on offer" and slip past the fills-the-width guard, so answer with
  // the space itself — an unmeasurable root is exactly what must not be hugged
  // on a guess.
  return widest > 0 ? widest : fullWidth;
}

/**
 * Lengths and units that resolve against the size of an ancestor container.
 * A digit has to precede the unit so that an identifier which merely contains
 * the letters — `--cqw-scale`, `.acqi` — does not read as a container length.
 */
const CONTAINER_UNIT_PATTERN = /\d(?:\.\d+)?\s*cq(?:w|h|i|b|min|max)\b/i;

interface ContainerDependenceCacheEntry {
  /** {@link styleSheetSignature} at the time the selectors were collected. */
  signature: string;
  /** {@link collectContainerDependentSelectors}. */
  selectors: string[] | null;
}

const containerDependenceCache = new WeakMap<
  ShadowRoot,
  ContainerDependenceCacheEntry
>();

/**
 * Cheap fingerprint of a set of style sheets, for deciding whether the
 * collected selectors are still current.
 *
 * Sheet counts alone would miss a rewrite in place: `insertRule`, `replaceSync`
 * on an adopted sheet, or new `textContent` on a `<style>` all change the rules
 * while leaving the sheet count identical, and a stale "does not depend on its
 * container" would let the frame hug a component that reflows. Rule counts
 * catch all three, and reading `cssRules.length` is an O(1) read on a live
 * list: it serializes nothing, which is what makes it usable on every pass
 * when the walk it guards is not.
 *
 * A rewrite that preserves the rule count — swapping `width: 100px` for
 * `width: 50cqw` in place — still slips through. Catching that needs the rule
 * text itself, which is the serialization this fingerprint exists to avoid.
 */
function styleSheetSignature(sheets: readonly CSSStyleSheet[]): string {
  return sheets
    .map((sheet) => {
      try {
        return String(sheet.cssRules.length);
      } catch {
        // Cross-origin. Its rules are unavailable, so a constant keeps the
        // signature stable across passes; `collectContainerDependentSelectors`
        // is what turns unreadability into an answer.
        return 'x';
      }
    })
    .join(',');
}

/**
 * The selectors of every style rule whose layout depends on the size of an
 * ancestor container: rules inside an `@container`, and rules that use `cq*`
 * units. Returns `null` when a sheet could not be read at all.
 *
 * Collecting selectors rather than answering yes/no lets the caller ask whether
 * a rule has anything to do with *this* component. The SITE loader gives every
 * component on a site the same bundle, so a single `@container` written for one
 * component would otherwise opt every other component out of hugging.
 */
export function collectContainerDependentSelectors(
  sheets: Iterable<CSSStyleSheet>
): string[] | null {
  const selectors: string[] = [];

  for (const sheet of sheets) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      // A cross-origin sheet throws rather than listing its rules. We cannot
      // rule out a container dependence inside it, and the cost of guessing
      // wrong is the component silently reflowing to its narrow layout.
      return null;
    }
    collectFrom(rules, false, selectors);
  }

  return selectors;
}

function collectFrom(
  rules: CSSRuleList,
  insideContainerRule: boolean,
  out: string[]
): void {
  for (const rule of Array.from(rules)) {
    // Duck-typed rather than `instanceof CSSContainerRule`: the rule belongs to
    // the canvas document's realm, where the constructor is a different object
    // than the one in scope here.
    const isContainerRule = 'containerQuery' in rule;

    const {selectorText} = rule as CSSStyleRule;
    if (
      selectorText != null &&
      (insideContainerRule || CONTAINER_UNIT_PATTERN.test(rule.cssText))
    ) {
      out.push(selectorText);
    }

    const nested = (rule as CSSGroupingRule).cssRules;
    if (nested != null) {
      collectFrom(nested, insideContainerRule || isContainerRule, out);
    }
  }
}

/**
 * Whether this component's layout depends on the width of the frame around it.
 *
 * The height comparison in {@link canHugCodeIsland} cannot see this: the
 * collapse pass sets `container-type: normal` on the frame (see
 * {@link FRAME_MEASUREMENTS_STYLE_TAG}), so a component's container queries and
 * `cq*` lengths resolve against the same ancestor in both probes and its height
 * comes back identical however narrow the host got. At rest the frame *is* the
 * container, so hugging it to the component's own width is what makes such a
 * component drop to its narrow layout on a desktop canvas.
 */
export function codeIslandDependsOnContainerSize(contentEl: Element): boolean {
  const {shadowRoot} = contentEl;
  if (shadowRoot == null) return false;

  const sheets = [
    ...Array.from(shadowRoot.styleSheets ?? []),
    ...Array.from(shadowRoot.adoptedStyleSheets ?? []),
  ];

  // Walking every rule on every pass is more work than the rest of the
  // measurement put together, and a component's style sheets almost never
  // change between passes.
  const signature = styleSheetSignature(sheets);
  let cached = containerDependenceCache.get(shadowRoot);
  if (cached == null || cached.signature !== signature) {
    cached = {
      signature,
      selectors: collectContainerDependentSelectors(sheets),
    };
    containerDependenceCache.set(shadowRoot, cached);
  }

  const {selectors} = cached;
  if (selectors == null) return true;

  return selectors.some((selector) =>
    selectorMatchesInShadowRoot(shadowRoot, selector)
  );
}

/**
 * Selector shapes a query from inside the shadow root cannot decide, and so
 * cannot be used to rule a container-dependent rule out as some other
 * component's:
 *
 * - `:host` targets the element the shadow root hangs off, out of reach from
 *   within it, and `&` is only meaningful in its nesting context.
 * - A pseudo-element matches no element at all, so `querySelector` answers
 *   `null` for `.card::after` whether or not this component has a `.card`.
 * - A state pseudo-class matches only while the state is live, and nothing is
 *   hovered, focused or checked while the measurement pass runs.
 *
 * Each of those would otherwise read as "not this component's rule", which is
 * the unsafe direction: the cost of being wrong is the component silently
 * reflowing on the canvas, the failure this whole gate exists to prevent. The
 * list is deliberately over-broad — a false positive costs one component its
 * hugging, a false negative costs correctness.
 */
const UNTESTABLE_SELECTOR_PATTERN =
  /:host|&|::|:(?:hover|focus|active|visited|link|target|checked|indeterminate|default|disabled|enabled|read-only|read-write|placeholder-shown|autofill|valid|invalid|in-range|out-of-range|required|optional|user-valid|user-invalid|open|popover-open|modal|fullscreen|picture-in-picture|defined)\b/;

function selectorMatchesInShadowRoot(
  shadowRoot: ShadowRoot,
  selector: string
): boolean {
  if (UNTESTABLE_SELECTOR_PATTERN.test(selector)) return true;

  try {
    return shadowRoot.querySelector(selector) != null;
  } catch {
    return true;
  }
}

/**
 * What {@link useFrameMeasurements} reads about one code component across its
 * two probes, in content-box terms.
 *
 * See also:
 * - {@link canHugCodeIsland}
 */
export interface CodeIslandHugProbe {
  /** Host content width with the Fit rules suspended. */
  fullWidth: number;
  /** Host content height with the Fit rules suspended. */
  fullHeight: number;
  /** {@link measureCodeIslandPaintedWidth}, read in that same state. */
  paintedWidth: number;
  /** Host content width with the Fit rules applied. */
  hugWidth: number;
  /** Host content height with the Fit rules applied. */
  hugHeight: number;
  /** {@link codeIslandDependsOnContainerSize}. */
  dependsOnContainerSize: boolean;
}

/**
 * Sub-pixel slack for the two comparisons below. Layout rounds, and a
 * component that is genuinely width-independent still reports heights that
 * differ in the last fraction of a pixel between probes.
 */
const HUG_TOLERANCE_PX = 0.5;

/**
 * Whether shrinking this code component to its own width is safe — that is,
 * whether the component the author sees afterwards is the same component.
 *
 * Hugging costs the component the width it was laid out in, and a frame is its
 * own container query container, so it is only safe for a component that does
 * not use that width for anything. Each check below is a way that assumption
 * fails; failing any one of them makes {@link useFrameMeasurements} size the
 * frame from the unhugged probe instead.
 */
export function canHugCodeIsland({
  fullWidth,
  fullHeight,
  paintedWidth,
  hugWidth,
  hugHeight,
  dependsOnContainerSize,
}: CodeIslandHugProbe): boolean {
  // Nothing to hug: the shrink left an empty content box, because the
  // component's root is entirely percentage- or absolutely-sized, or because
  // it has not rendered yet. Hugging reports a sliver.
  if (hugWidth <= 0) return false;

  // Responsive to its container. See `codeIslandDependsOnContainerSize` for
  // why the height check below cannot stand in for this one.
  if (dependsOnContainerSize) return false;

  // Fills the width it is given: a `width: 100%` root, or a flex/grid root
  // whose tracks are all flexible. Its width is not a size it chose, so there
  // is nothing there to hug — and shrinking it re-resolves the percentage
  // against a shrink-to-fit box, which collapses it toward its gaps.
  if (paintedWidth >= fullWidth - HUG_TOLERANCE_PX) return false;

  // Derives its height from its width: an `aspect-ratio` root, a wrapping
  // paragraph, a grid that reflows. The height read while shrunk is not the
  // height the component has at full width, so hugging would size the frame
  // for a layout the author never sees.
  if (Math.abs(hugHeight - fullHeight) > HUG_TOLERANCE_PX) return false;

  return true;
}

/**
 * Records {@link canHugCodeIsland}'s answer on the host, for the Fit rule in
 * {@link FRAME_MEASUREMENTS_STYLE_TAG} to read outside the measurement pass.
 * Writes only on a change; see the call site in {@link useFrameMeasurements}
 * for why.
 */
export function syncCodeIslandNoHugAttribute(
  contentEl: Element,
  cannotHug: boolean
): void {
  if (cannotHug) {
    if (
      contentEl.getAttribute(CODE_ISLAND_NO_HUG_ATTRIBUTE) !==
      CODE_ISLAND_NO_HUG_VALUE
    ) {
      contentEl.setAttribute(
        CODE_ISLAND_NO_HUG_ATTRIBUTE,
        CODE_ISLAND_NO_HUG_VALUE
      );
    }
  } else if (contentEl.hasAttribute(CODE_ISLAND_NO_HUG_ATTRIBUTE)) {
    contentEl.removeAttribute(CODE_ISLAND_NO_HUG_ATTRIBUTE);
  }
}

/**
 * The geometry {@link useFrameMeasurements} reads for a single frame, in the
 * viewport coordinates that apply *during* the collapse pass.
 *
 * See also:
 * - {@link computeFrameMeasurement}
 */
export interface FrameMeasurementInput {
  /** Bounding rect origin of the Frame Layout container. */
  layout: {left: number; top: number};
  frame: {
    scrollWidth: number;
    scrollHeight: number;
    /** Bounding rect of the frame element. */
    width: number;
    height: number;
    left: number;
    top: number;
  };
  content: {
    scrollWidth: number;
    scrollHeight: number;
    /** Bounding rect of `frame.children[0].children[0]`. */
    width: number;
    height: number;
    right: number;
    bottom: number;
    marginRight: number;
    marginBottom: number;
  };
}

/**
 * Turns one frame's measured geometry into the width/height the frame should
 * take in Fit mode.
 *
 * We take the maximum of scroll width and bounding client rect width of both
 * the frame and its first child and treat that as our measurement.
 *
 * This is imperfect in cases where deeply nested content is positioned
 * absolutely, but it works well for the common cases we've decided to care
 * about (namely nav bars).
 *
 * ## Why the content term is relative to the *layout*, not the frame
 *
 * A frame's virtual viewport (see `Frame`) is `position: fixed`, and its
 * containing block is the frame element only because the frame carries a
 * `transform` and an inline-size container (see `PrimitiveFrame`). The collapse
 * pass neutralises both, so during measurement every viewport escapes to the
 * initial containing block and all frames' content lands on the same origin as
 * the layout container. `content.right - frame.left + leftOffset` collapses to
 * `content.right - layout.left`, which is that content's own width whatever
 * position its frame holds in the grid.
 *
 * Dropping the offset would read `content.right - frame.left` and so
 * under-measure every frame after the first by exactly its own offset in the
 * grid. `FrameMeasurements.test.ts` pins this on both axes.
 *
 * See also:
 * - {@link useFrameMeasurements}
 * - {@link FrameMeasurementInput}
 */
export function computeFrameMeasurement({
  layout,
  frame,
  content,
}: FrameMeasurementInput): {
  width: number | 'max-content';
  height: number | 'max-content';
} {
  const topOffset = frame.top - layout.top;
  const leftOffset = frame.left - layout.left;

  let newWidth: number | 'max-content' =
    Math.max(
      frame.scrollWidth,
      frame.width,
      content.scrollWidth,
      content.width,
      content.right - frame.left + leftOffset
    ) + content.marginRight;
  let newHeight: number | 'max-content' =
    Math.max(
      frame.scrollHeight,
      frame.height,
      content.scrollHeight,
      content.height,
      content.bottom - frame.top + topOffset
    ) + content.marginBottom;

  if (frame.scrollWidth === newWidth) {
    newWidth = 'max-content';
  }
  if (frame.scrollHeight === newHeight) {
    newHeight = 'max-content';
  }

  return {width: newWidth, height: newHeight};
}

/**
 * Reads one frame's geometry out of the DOM, in the form
 * {@link computeFrameMeasurement} consumes. Must be called inside the collapse
 * pass; see {@link computeFrameMeasurement} for the coordinates that implies.
 *
 * Returns `null` for a frame whose virtual viewport or content wrapper is not
 * in the DOM, which has nothing to measure.
 *
 * See also:
 * - {@link useFrameMeasurements}
 */
function readFrameGeometry(
  frame: Element,
  layout: {left: number; top: number}
): {
  geometry: FrameMeasurementInput;
  /** `frame.children[0].children[0]`, the element the geometry describes. */
  contentEl: Element;
  /** Its bounding rect, so callers do not force a second layout to re-read it. */
  contentRect: DOMRect;
} | null {
  const contentEl = frame.children[0]?.children[0];
  if (contentEl == null) return null;

  const {scrollWidth: frameScrollWidth, scrollHeight: frameScrollHeight} =
    frame;
  const {
    width: frameBoundingWidth,
    height: frameBoundingHeight,
    top: frameTop,
    left: frameLeft,
  } = frame.getBoundingClientRect();

  const {scrollWidth: childScrollWidth, scrollHeight: childScrollHeight} =
    contentEl;
  const contentRect = contentEl.getBoundingClientRect();

  const {
    marginRight: childMarginRightString,
    marginBottom: childMarginBottomString,
  } = getComputedStyle(contentEl);

  return {
    contentEl,
    contentRect,
    geometry: {
      layout,
      frame: {
        scrollWidth: frameScrollWidth,
        scrollHeight: frameScrollHeight,
        width: frameBoundingWidth,
        height: frameBoundingHeight,
        left: frameLeft,
        top: frameTop,
      },
      content: {
        scrollWidth: childScrollWidth,
        scrollHeight: childScrollHeight,
        width: contentRect.width,
        height: contentRect.height,
        right: contentRect.right,
        bottom: contentRect.bottom,
        marginRight: parseFloat(childMarginRightString) || 0,
        marginBottom: parseFloat(childMarginBottomString) || 0,
      },
    },
  };
}

function selectSelectedRule(state: DesignerStores) {
  return state.ExternalStore.rule;
}

function selectMediaQuery(state: DesignerStores) {
  return state.ExternalStore.mediaQuery;
}

function selectStyleBlockState(state: DesignerStores) {
  return state.StyleBlockStore;
}

function selectCssVariablesState(state: DesignerStores) {
  return state.CssVariablesStore;
}

function selectComponents(state: DesignerStores) {
  return state.DesignerStore.components;
}

function selectContentEditorState(state: DesignerStores) {
  return state.ContentEditorStore;
}

/**
 * Hook that measures all frames inside a container (provided via the `ref`
 * parameter) and returns the measurements.
 *
 * The measurements should be stored in the {@link FrameMeasurementsContext}
 * context.
 *
 * IMPORTANT: The `FRAME_MEASUREMENTS_STYLE_TAG` must be included in the
 * container's DOM to ensure that a special CSS rule is temporarily applied to
 * the frames to ensure correct measurements are taken.
 *
 * See also:
 * - {@link FRAME_MEASUREMENTS_STYLE_TAG}
 * - {@link FrameMeasurements}
 * - {@link FrameMeasurementsContext}
 */
function useFrameMeasurements(): {
  frameMeasurements: FrameMeasurements;
  ref: React.RefObject<HTMLDivElement>;
} {
  const ref = React.useRef<HTMLDivElement>(null);
  const [frameMeasurements, setFrameMeasurements] =
    React.useState<FrameMeasurements>(Immutable.Map());

  const frameMeasurementsRef =
    React.useRef<FrameMeasurements>(frameMeasurements);

  React.useEffect(() => {
    frameMeasurementsRef.current = frameMeasurements;
  }, [frameMeasurements]);

  // We use a ref to track whether we need to measure, and in a rAF loop we
  // actually perform the measurement. This allows us to trigger remeasurement
  // with a mixture of different triggers while avoiding remeasuring multiple
  // times per frame unnecessarily:
  const needsMeasure = React.useRef(false);
  const queueMeasurement = React.useCallback(() => {
    needsMeasure.current = true;
  }, []);

  const measure = React.useCallback(
    function measure_() {
      if (ref.current == null) return;

      const allFrames = Array.from(
        ref.current.querySelectorAll(`[${WF_FRAME_ID_ATTRIBUTE}]`)
      ).filter(
        (frame) => frame instanceof frame.ownerDocument.defaultView!.HTMLElement
      );

      const fitFrames = new Set(
        allFrames.filter(
          (frame) =>
            frame.dataset.wfFrameWidth === 'max-content' ||
            frame.dataset.wfFrameHeight === 'max-content'
        )
      );

      // Special-case optimization: If there are no fit frames, we can skip all
      // DOM mutations and measurements and simply clear out our measurements:
      if (fitFrames.size === 0) {
        setFrameMeasurements(Immutable.Map());
        return;
      }

      ref.current.classList.add(COLLAPSE_ALL_FRAMES_CLASS);

      let newFrameMeasurements = frameMeasurementsRef.current;

      const {top: layoutTop, left: layoutLeft} =
        ref.current.getBoundingClientRect();
      const layout = {left: layoutLeft, top: layoutTop};

      // The Fit rules only touch a code component in a frame that fits on the
      // width axis, so those are the only frames the unhugged probe below has
      // anything to say about.
      const codeIslandFrames = Array.from(fitFrames).filter((frame) => {
        if (frame.dataset.wfFrameWidth !== 'max-content') return false;
        const contentEl = frame.children[0]?.children[0];
        return contentEl != null && isCodeIsland(contentEl);
      });

      // Unhugged probe. `canHugCodeIsland` decides from the difference between
      // a component's hugged and unhugged layout, so the pass has to see both;
      // and a frame it decides against is then measured from this geometry,
      // read with the Fit rules suspended and the component unshrunk.
      const unhuggedReads = new Map<
        Element,
        {
          geometry: FrameMeasurementInput;
          probe: Omit<CodeIslandHugProbe, 'hugWidth' | 'hugHeight'>;
        }
      >();
      if (codeIslandFrames.length > 0) {
        ref.current.classList.add(SUPPRESS_CODE_ISLAND_HUG_CLASS);

        for (const frame of codeIslandFrames) {
          const read = readFrameGeometry(frame, layout);
          if (read == null) continue;

          const {width, height} = codeIslandContentBoxSize(
            read.contentEl,
            read.contentRect
          );

          unhuggedReads.set(frame, {
            geometry: read.geometry,
            probe: {
              fullWidth: width,
              fullHeight: height,
              paintedWidth: measureCodeIslandPaintedWidth(
                read.contentEl,
                width
              ),
              dependsOnContainerSize: codeIslandDependsOnContainerSize(
                read.contentEl
              ),
            },
          });
        }

        ref.current.classList.remove(SUPPRESS_CODE_ISLAND_HUG_CLASS);
      }

      for (const frame of allFrames) {
        const frameId = frame.dataset.wfFrameId;
        if (frameId == null) continue;

        if (!fitFrames.has(frame)) {
          newFrameMeasurements = newFrameMeasurements.delete(frameId);
          continue;
        }

        const read = readFrameGeometry(frame, layout);
        if (read == null) {
          newFrameMeasurements = newFrameMeasurements.delete(frameId);
          continue;
        }

        let {geometry} = read;

        const unhugged = unhuggedReads.get(frame);
        if (unhugged != null) {
          const {width: hugWidth, height: hugHeight} = codeIslandContentBoxSize(
            read.contentEl,
            read.contentRect
          );
          const canHug = canHugCodeIsland({
            ...unhugged.probe,
            hugWidth,
            hugHeight,
          });

          // Both passes shrink every code island regardless of what the last
          // one decided, so this re-decides from the same starting point every
          // time rather than reading back the state it last wrote. Writing the
          // attribute only on a change keeps the DOM still once settled, so the
          // mutation observer does not queue another pass.
          syncCodeIslandNoHugAttribute(read.contentEl, !canHug);

          if (!canHug) {
            geometry = unhugged.geometry;
          }
        }

        const {width: newWidth, height: newHeight} =
          computeFrameMeasurement(geometry);

        const measurements = newFrameMeasurements.get(frameId);
        if (
          measurements?.width !== newWidth ||
          measurements?.height !== newHeight
        ) {
          newFrameMeasurements = newFrameMeasurements.set(frameId, {
            width: newWidth,
            height: newHeight,
          });
        }
      }

      ref.current.classList.remove(COLLAPSE_ALL_FRAMES_CLASS);

      if (newFrameMeasurements !== frameMeasurementsRef.current) {
        setFrameMeasurements(newFrameMeasurements);
      }
    },
    [ref]
  );

  React.useEffect(() => {
    let raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };

    function tick() {
      if (needsMeasure.current) {
        measure();
        needsMeasure.current = false;
      }

      raf = requestAnimationFrame(tick);
    }
  }, [measure]);

  React.useEffect(() => {
    if (ref.current == null) return;

    function handleDomMutation(mutations: MutationRecord[]) {
      // Ignore mutations during measurement:
      if (ref.current?.classList.contains(COLLAPSE_ALL_FRAMES_CLASS)) {
        return;
      }

      // Ignore mutations to our main ref element and its first two wrappers:
      if (
        mutations.every(
          ({target}) =>
            target === ref.current ||
            target.parentElement === ref.current ||
            target.parentElement?.parentElement === ref.current
        )
      ) {
        return;
      }

      queueMeasurement();
    }

    const mutationObserver = new MutationObserver(handleDomMutation);
    mutationObserver.observe(ref.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // We also schedule a fallback periodic measurement to pick up things that
    // are harder to detect:
    const interval = setInterval(queueMeasurement, 1000);

    return () => {
      mutationObserver.disconnect();
      clearInterval(interval);
    };
  }, [queueMeasurement]);

  // Any time any of these selectors change, we schedule frame remeasurement:
  const externalStateSelectedRule = useDesignerSelector(selectSelectedRule);
  const styleBlockState = useDesignerSelector(selectStyleBlockState);
  const cssVariablesState = useDesignerSelector(selectCssVariablesState);
  const components = useDesignerSelector(selectComponents);
  const contentEditorState = useDesignerSelector(selectContentEditorState);
  const mediaQuery = useDesignerSelector(selectMediaQuery);

  const flux = useFluxContext<DesignerFlux>();

  React.useLayoutEffect(() => {
    // We optimistically measure synchronously; this usually is enough, but
    // sometimes actions fire indicating that the style sheet or canvas has been
    // updated at some point after this:
    cleanupAndMeasure();

    // First, we try and wait for our canvas or style sheet to be re-rendered
    // before measuring, as a change in state should do that for us, but not
    // synchronously:
    flux.dispatcher.register(remeasureWhenRerenderDetected);

    return () => {
      cleanup();
    };

    function remeasureWhenRerenderDetected(action: unknown) {
      if (typeof action !== 'object' || action == null || !('type' in action))
        return;

      const type = action?.type;
      if (type === CANVAS_BODY_RENDERED || type === STYLE_SHEET_RENDERED) {
        // If we're here, it means the canvas or style sheet has been
        // re-rendered, so we can measure immediately now that the DOM/CSSOM is
        // synced with state:
        cleanupAndMeasure();
      }
    }

    function cleanup() {
      // Unregister our flux listener since we're about to measure anyway:
      flux.dispatcher.unregister(remeasureWhenRerenderDetected);
    }

    function cleanupAndMeasure() {
      cleanup();
      queueMeasurement();
    }
  }, [
    externalStateSelectedRule,
    mediaQuery,
    styleBlockState,
    cssVariablesState,
    components,
    contentEditorState,
    queueMeasurement,
    flux.dispatcher,
  ]);

  // Subscribes to Frame size transitions, triggering an effect to
  // remeasure frames whenever there are changes to Frame sizes in `FrameSizeTransitionStore`.
  // This supports Frames with `Fit` mode dimensions to re-measure and update their size
  // while an on-canvas Frame resize interaction is in progress.
  useFrameSizeTransitionEffect(queueMeasurement);

  return {frameMeasurements, ref};
}

/**
 * A CSS style tag that is used to apply a special CSS rule to the frames to
 * ensure correct measurements are taken.
 *
 * See also:
 * - {@link useFrameMeasurements}
 */
const FRAME_MEASUREMENTS_STYLE_TAG = (
  <style>{`
  /* Ignore pointer events on the frame viewport to make it transparent to DnD: */
  [data-wf-frame-viewport="true"] {
    pointer-events: none;
    & > * {
      pointer-events: auto;
    }
  }

  #${OPEN_CANVAS_ROOT_ID}:has(.${COLLAPSE_ALL_FRAMES_CLASS}) {
    transform: none!important;
  }

  /* Deliberately unscoped: it has to hold outside the measurement pass too. A
     Fit-width frame pins its viewport to the breakpoint width so percentages
     resolve consistently (see \`Frame\`), and a code component host is a block
     box, so it stretches to that width and Fit can never measure anything but
     the breakpoint. Shrinking the host to its own content is what lets Fit
     measure the component, with a gutter so it is not flush against the frame.
     Scoping this to the collapse pass would measure the frame hugged and then
     let the host snap back to breakpoint width inside it.

     Two consequences of being unscoped. It reaches a frame an author had
     already set to Fit, which today cannot hug a code island at all: such
     frames start hugging and gain the gutter. And "unscoped" still means edit
     mode only — \`FrameLayout\` mounts \`FrameMeasurer\` (and so this tag) only
     when \`!isPreviewMode\`, and \`Frame\` drops the \`data-wf-frame-viewport\`
     wrapper in preview, so in preview the component renders flush at the
     frame's own width, 2 x CODE_COMPONENT_FIT_FRAME_PADDING narrower.

     Shrinking is not safe for every component — a root that fills the width it
     is given collapses, one that derives height from width reports a height the
     author never sees, and one that depends on its container reflows, because a
     frame is its own container. CSS cannot tell any of those from "wants to be
     small", so \`canHugCodeIsland\` decides from a probe of the component in
     both states and the pass records the answer as
     \`${CODE_ISLAND_NO_HUG_ATTRIBUTE}\`. The second selector below keeps the
     shrink applied during the pass whatever that attribute says, so each pass
     re-decides from the same starting point instead of latching.

     The child combinators track the DOM the measurement pass reads
     (\`frame.children[0].children[0]\`) and the host/viewport relationship
     asserted by \`shouldUseCodeIslandHostForMeasurement\`. A descendant
     combinator would keep matching if a wrapper were inserted between the two,
     but the measurement would then read the wrapper — better that both stop
     matching together than that they silently diverge. */
  [data-wf-frame-width="max-content"] > [data-wf-frame-viewport="true"] > code-island:not([${CODE_ISLAND_NO_HUG_ATTRIBUTE}="${CODE_ISLAND_NO_HUG_VALUE}"]),
  .${COLLAPSE_ALL_FRAMES_CLASS} > [data-wf-frame-width="max-content"] > [data-wf-frame-viewport="true"] > code-island {
    width: fit-content;
    padding: ${CODE_COMPONENT_FIT_FRAME_PADDING}px;
  }

  /* Suspends both rules above for the unhugged probe, so the pass can read
     each code component unshrunk: one half of the comparison
     \`canHugCodeIsland\` decides on, and the geometry a frame it decides
     against is measured from. */
  .${SUPPRESS_CODE_ISLAND_HUG_CLASS} [data-wf-frame-viewport="true"] > code-island {
    width: auto!important;
    padding: 0!important;
  }

  .${COLLAPSE_ALL_FRAMES_CLASS} > [data-wf-frame-id] {
    transform: none!important;
    overflow: visible!important;
    /* Suspend inline-size containment so \`width: max-content\` resolves
       against descendants during the Fit-mode measurement pass. (Block
       axis isn't contained under \`inline-size\`, so \`height: max-content\`
       would work without this override — defending both axes here keeps
       the rule self-contained.) */
    container-type: normal!important;
    &[data-wf-frame-width="max-content"] {
      width: max-content!important;
    }
    &[data-wf-frame-height="max-content"] {
      height: max-content!important;
    }
  }
`}</style>
);

const DEFAULT_FRAME_MEASUREMENTS = {
  width: 'max-content',
  height: 'max-content',
} as const;

interface DimensionSetting {
  mode: {tag: 'fixed' | 'full' | 'fit'} | undefined;
  value: number;
}

/**
 * A breakpoint's width as a CSS length. Shared by Full and, in preview, by Fit,
 * which resolves to the same width — see {@link resolveFitWidthForPreview}. The
 * two have to agree, so they read it from here rather than each deriving it.
 */
const breakpointWidthPx = (breakpointId: string): string => {
  const breakpoint: BreakpointID = getBreakpointID(breakpointId) ?? 'main';
  return `${getBreakpointWidth(breakpoint)}px`;
};

const getDimension = (
  setting: DimensionSetting,
  breakpointId?: string
): string => {
  const tag = setting.mode?.tag;

  if (tag === 'fixed') {
    return `${setting.value}px`;
  }

  if (tag === 'fit') {
    return 'max-content';
  }

  if (setting.mode?.tag === 'full' && breakpointId) {
    return breakpointWidthPx(breakpointId);
  }

  return 'auto';
};

/**
 * Resolves a Fit (`max-content`) frame width for preview.
 *
 * Fit relies on the measurement pass to hand the frame a concrete width, and
 * that pass is edit-mode only: `FrameLayout` mounts `FrameMeasurer` just when
 * `!isPreviewMode`. So preview puts `max-content` on the frame element itself,
 * where `PrimitiveFrame`'s inline-size containment means no descendant can
 * contribute to it — it resolves against nothing and the frame collapses to
 * `MIN_FRAME_WIDTH_PX`, a 16px sliver of the component.
 *
 * The breakpoint width is what a Fit frame's content is laid out against in
 * edit mode too, since the frame viewport it escapes to is breakpoint-sized
 * (see `Frame`). So a component that fills the width it is given previews at
 * the width it was designed at, which covers every component a frame
 * `canHugCodeIsland` rules out — including one freshly seeded Fit by
 * `CODE_COMPONENT_CREATED`. A component the frame does hug still previews wider
 * than it renders in edit mode; preview has nothing measured to hug with.
 *
 * Only the inline axis needs this, the block axis being uncontained.
 */
export function resolveFitWidthForPreview(
  width: string,
  breakpointId: string
): string {
  return width === 'max-content' ? breakpointWidthPx(breakpointId) : width;
}

export function getUserSuppliedDimensions(
  frameData: any,
  breakpointId: string
): {
  width: string;
  height: string;
} {
  const dimensions = frameData.size[breakpointId];
  const width = dimensions?.width
    ? getDimension(dimensions.width, breakpointId)
    : 'auto';
  const height = dimensions?.height
    ? getDimension(dimensions.height, breakpointId)
    : 'auto';
  return {width, height};
}

/**
 * Hook that returns the styles (`width` and `height`) to be applied to a
 * frame's DOM element to ensure it is sized correctly.
 *
 * The width and height values that are passed in are what the user has
 * configured the frame's size to be.
 *
 * See also:
 * - {@link getMeasuredFrameDimensionAttributes}
 */
export function useMeasuredFrameDimensionStyles({
  frameId,
  breakpointId,
  frameData,
}: {
  frameId: string;
  breakpointId: string;
  /**
   * The evaluated `data` for the frame element being rendered.
   */
  frameData: any;
}): {
  width: number | string;
  height: number | string;
} {
  const selector = React.useCallback(
    (
      measurements: FrameMeasurements
    ): {width: number | 'max-content'; height: number | 'max-content'} =>
      measurements.get(frameId) ?? DEFAULT_FRAME_MEASUREMENTS,
    [frameId]
  );

  const scrollDimensions = FrameMeasurementsContext.useContext(selector);
  const {width, height} = getUserSuppliedDimensions(frameData, breakpointId);

  const isPreviewMode = usePreviewMode();
  if (isPreviewMode) {
    return {
      width: resolveFitWidthForPreview(width, breakpointId),
      height,
    };
  }

  return {
    width: width === 'max-content' ? scrollDimensions.width : width,
    height: height === 'max-content' ? scrollDimensions.height : height,
  };
}

/**
 * Returns the attributes to be applied to a frame's DOM element to ensure
 * correct measurements are taken.
 *
 * See also:
 * - {@link useMeasuredFrameDimensionStyles}
 * - {@link useFrameMeasurements}
 */
export function getMeasuredFrameDimensionAttributes({
  frameData,
  breakpointId,
}: {
  /**
   * The evaluated `data` for the frame element being rendered.
   */
  frameData: any;
  breakpointId: string;
}) {
  const {width, height} = getUserSuppliedDimensions(frameData, breakpointId);

  return {
    'data-wf-frame-width': width === 'max-content' ? width : parseFloat(width),
    'data-wf-frame-height':
      height === 'max-content' ? height : parseFloat(height),
  };
}

export function FrameMeasurer({
  children,
}: {
  children: (ref: React.RefObject<HTMLDivElement>) => React.ReactNode;
}) {
  const {ref, frameMeasurements} = useFrameMeasurements();

  return (
    <>
      {FRAME_MEASUREMENTS_STYLE_TAG}
      <FrameMeasurementsContext.ContextProvider value={frameMeasurements}>
        {children(ref)}
      </FrameMeasurementsContext.ContextProvider>
    </>
  );
}
